import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { useChartOfAccountsExport } from '@/hooks/useChartOfAccountsExport';
import { z } from 'zod';
import ExcelJS from 'exceljs';
import { useSupabaseSync } from '@/hooks/useSupabaseSync';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { useFinance } from '@/context/FinanceContext';

interface ImportChartOfAccountsButtonProps {
  onImportSuccess: () => void;
}

// Validation schema for each row - ALL fields are mandatory
const chartOfAccountsRowSchema = z.object({
  codigoNivel1: z.string().trim().min(1, 'Código Nível 1 é obrigatório'),
  nomeNivel1: z.string().trim().min(1, 'Nome Nível 1 é obrigatório'),
  codigoNivel2: z.string().trim().min(1, 'Código Nível 2 é obrigatório'),
  nomeNivel2: z.string().trim().min(1, 'Nome Nível 2 é obrigatório'),
  codigoNivel3: z.string().trim().min(1, 'Código Nível 3 é obrigatório'),
  nomeNivel3: z.string().trim().min(1, 'Nome Nível 3 é obrigatório'),
  tipo: z.enum(['receita', 'despesa'], {
    errorMap: () => ({ message: 'Tipo deve ser "receita" ou "despesa"' }),
  }),
});

interface ParsedRow {
  codigoNivel1: string;
  nomeNivel1: string;
  codigoNivel2: string;
  nomeNivel2: string;
  codigoNivel3: string;
  nomeNivel3: string;
  tipo: 'receita' | 'despesa';
}

// Helper to extract cell value properly
const getCellValue = (cell: ExcelJS.Cell): any => {
  if (cell.value === null || cell.value === undefined) return undefined;
  
  // Handle rich text
  if (typeof cell.value === 'object' && 'richText' in cell.value) {
    return (cell.value as ExcelJS.CellRichTextValue).richText.map(rt => rt.text).join('');
  }
  
  // Handle formula results
  if (typeof cell.value === 'object' && 'result' in cell.value) {
    return (cell.value as ExcelJS.CellFormulaValue).result;
  }
  
  // Handle hyperlinks
  if (typeof cell.value === 'object' && 'hyperlink' in cell.value) {
    return (cell.value as ExcelJS.CellHyperlinkValue).text;
  }
  
  return cell.value;
};

export const ImportChartOfAccountsButton: React.FC<ImportChartOfAccountsButtonProps> = ({
  onImportSuccess,
}) => {
  const [open, setOpen] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [previewData, setPreviewData] = React.useState<ParsedRow[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { downloadExampleExcel } = useChartOfAccountsExport();
  const supabaseSync = useSupabaseSync();
  const { user } = useAuth();
  const { forceRefresh } = useFinance();

  const resetState = () => {
    setErrors([]);
    setSelectedFile(null);
    setPreviewData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetState();
    }
  };

  const readExcelFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(data);
          
          const worksheet = workbook.worksheets[0];
          if (!worksheet) {
            reject(new Error('Planilha não encontrada no arquivo'));
            return;
          }

          const rows: any[] = [];
          let headers: string[] = [];
          let isFirstRow = true;
          
          worksheet.eachRow((row, rowNumber) => {
            if (isFirstRow) {
              // First row is header
              isFirstRow = false;
              const rowValues = row.values as any[];
              headers = rowValues.slice(1).map((v, i) => {
                const cell = row.getCell(i + 1);
                const value = getCellValue(cell);
                return String(value || '');
              });
            } else {
              // Data rows - filter out empty rows
              const rowData: any = {};
              let hasData = false;
              
              headers.forEach((header, index) => {
                const cell = row.getCell(index + 1);
                const value = getCellValue(cell);
                rowData[header] = value;
                if (value !== undefined && value !== '') {
                  hasData = true;
                }
              });
              
              if (hasData) {
                rows.push(rowData);
              }
            }
          });

          if (rows.length < 1) {
            reject(new Error('Arquivo deve conter cabeçalho e pelo menos uma linha de dados'));
            return;
          }

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
      reader.readAsArrayBuffer(file);
    });
  };

  const validateAndParseData = (rawData: any[]): { valid: boolean; data: ParsedRow[]; errors: string[] } => {
    const validationErrors: string[] = [];
    const parsedRows: ParsedRow[] = [];

    // Track unique codes to detect duplicates
    const level1Codes = new Map<string, number>();
    const level2Codes = new Map<string, number>();
    const level3Codes = new Map<string, number>();

    rawData.forEach((row, index) => {
      const rowNum = index + 2; // +2 for 0-based index and header row

      // Normalize column names (handle different possible headers)
      const normalizedRow = {
        codigoNivel1: String(row['Código Nível 1'] ?? row['codigoNivel1'] ?? row['Codigo Nivel 1'] ?? '').trim(),
        nomeNivel1: String(row['Nome Nível 1'] ?? row['nomeNivel1'] ?? row['Nome Nivel 1'] ?? '').trim(),
        codigoNivel2: String(row['Código Nível 2'] ?? row['codigoNivel2'] ?? row['Codigo Nivel 2'] ?? '').trim(),
        nomeNivel2: String(row['Nome Nível 2'] ?? row['nomeNivel2'] ?? row['Nome Nivel 2'] ?? '').trim(),
        codigoNivel3: String(row['Código Nível 3'] ?? row['codigoNivel3'] ?? row['Codigo Nivel 3'] ?? '').trim(),
        nomeNivel3: String(row['Nome Nível 3'] ?? row['nomeNivel3'] ?? row['Nome Nivel 3'] ?? '').trim(),
        tipo: String(row['Tipo'] ?? row['tipo'] ?? row['Type'] ?? '').toLowerCase().trim() as 'receita' | 'despesa',
      };

      // Validate with Zod schema
      const result = chartOfAccountsRowSchema.safeParse(normalizedRow);

      if (!result.success) {
        const errorMessages = result.error.errors.map(e => e.message).join('; ');
        validationErrors.push(`Linha ${rowNum}: ${errorMessages}`);
        return;
      }

      const validRow = result.data;

      // Validate code hierarchy - Level 2 must start with Level 1 code
      if (!validRow.codigoNivel2.startsWith(validRow.codigoNivel1 + '.')) {
        validationErrors.push(
          `Linha ${rowNum}: Código Nível 2 (${validRow.codigoNivel2}) deve começar com o código do Nível 1 (${validRow.codigoNivel1}.)`
        );
        return;
      }

      // Validate code hierarchy - Level 3 must start with Level 2 code
      if (!validRow.codigoNivel3.startsWith(validRow.codigoNivel2 + '.')) {
        validationErrors.push(
          `Linha ${rowNum}: Código Nível 3 (${validRow.codigoNivel3}) deve começar com o código do Nível 2 (${validRow.codigoNivel2}.)`
        );
        return;
      }

      // Track codes for duplicate detection
      level1Codes.set(validRow.codigoNivel1, (level1Codes.get(validRow.codigoNivel1) || 0) + 1);
      level2Codes.set(validRow.codigoNivel2, (level2Codes.get(validRow.codigoNivel2) || 0) + 1);
      
      // Check for duplicate Level 3 codes
      if (level3Codes.has(validRow.codigoNivel3)) {
        validationErrors.push(
          `Linha ${rowNum}: Código Nível 3 duplicado no arquivo (${validRow.codigoNivel3})`
        );
        return;
      }
      level3Codes.set(validRow.codigoNivel3, rowNum);

      parsedRows.push({
        codigoNivel1: validRow.codigoNivel1,
        nomeNivel1: validRow.nomeNivel1,
        codigoNivel2: validRow.codigoNivel2,
        nomeNivel2: validRow.nomeNivel2,
        codigoNivel3: validRow.codigoNivel3,
        nomeNivel3: validRow.nomeNivel3,
        tipo: validRow.tipo,
      });
    });

    return {
      valid: validationErrors.length === 0,
      data: parsedRows,
      errors: validationErrors,
    };
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setErrors([]);
    setPreviewData([]);

    try {
      const rawData = await readExcelFile(file);
      const { valid, data, errors: validationErrors } = validateAndParseData(rawData);

      if (!valid) {
        setErrors(validationErrors);
        return;
      }

      setPreviewData(data);
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Erro ao processar arquivo']);
    }
  };

  const handleImport = async () => {
    if (!user || previewData.length === 0) return;

    setImporting(true);
    setErrors([]);

    try {
      // Fetch existing data to check for duplicates
      const [existingL1, existingL2, existingL3] = await Promise.all([
        supabaseSync.fetchChartOfAccountsLevel1(),
        supabaseSync.fetchChartOfAccountsLevel2(),
        supabaseSync.fetchChartOfAccounts(),
      ]);

      const existingL1Codes = new Set(existingL1.map(l => l.code.toLowerCase()));
      const existingL2Codes = new Set(existingL2.map(l => l.code.toLowerCase()));
      const existingL3Codes = new Set(existingL3.map(l => l.code?.toLowerCase()).filter(Boolean));

      // Group data for import
      const level1Map = new Map<string, { code: string; name: string; id?: string }>();
      const level2Map = new Map<string, { code: string; name: string; type: 'receita' | 'despesa'; level1Id: string; id?: string }>();
      const level3ToCreate: { code: string; name: string; type: 'receita' | 'despesa'; level2Code: string }[] = [];

      const importErrors: string[] = [];

      // Process each row
      previewData.forEach((row, index) => {
        const rowNum = index + 2;

        // Check for existing Level 3 codes in database
        if (existingL3Codes.has(row.codigoNivel3.toLowerCase())) {
          importErrors.push(`Linha ${rowNum}: Código Nível 3 (${row.codigoNivel3}) já existe no sistema`);
          return;
        }

        // Add Level 1 to map (will be created or found)
        if (!level1Map.has(row.codigoNivel1)) {
          if (existingL1Codes.has(row.codigoNivel1.toLowerCase())) {
            // Use existing Level 1
            const existing = existingL1.find(l => l.code.toLowerCase() === row.codigoNivel1.toLowerCase());
            level1Map.set(row.codigoNivel1, { code: row.codigoNivel1, name: row.nomeNivel1, id: existing?.id });
          } else {
            level1Map.set(row.codigoNivel1, { code: row.codigoNivel1, name: row.nomeNivel1 });
          }
        }

        // Add Level 2 to map (will be created or found)
        if (!level2Map.has(row.codigoNivel2)) {
          if (existingL2Codes.has(row.codigoNivel2.toLowerCase())) {
            // Use existing Level 2
            const existing = existingL2.find(l => l.code.toLowerCase() === row.codigoNivel2.toLowerCase());
            level2Map.set(row.codigoNivel2, {
              code: row.codigoNivel2,
              name: row.nomeNivel2,
              type: row.tipo,
              level1Id: '', // Will be filled later
              id: existing?.id,
            });
          } else {
            level2Map.set(row.codigoNivel2, {
              code: row.codigoNivel2,
              name: row.nomeNivel2,
              type: row.tipo,
              level1Id: row.codigoNivel1,
            });
          }
        }

        // Add Level 3 to create list
        level3ToCreate.push({
          code: row.codigoNivel3,
          name: row.nomeNivel3,
          type: row.tipo,
          level2Code: row.codigoNivel2,
        });
      });

      if (importErrors.length > 0) {
        setErrors(importErrors);
        setImporting(false);
        return;
      }

      // Create Level 1 accounts
      const level1IdMap = new Map<string, string>();
      for (const [code, l1] of level1Map) {
        if (l1.id) {
          level1IdMap.set(code, l1.id);
        } else {
          const id = await supabaseSync.createChartOfAccountLevel1({ code: l1.code, name: l1.name });
          if (id) {
            level1IdMap.set(code, id);
          } else {
            importErrors.push(`Erro ao criar Nível 1: ${l1.code} - ${l1.name}`);
          }
        }
      }

      if (importErrors.length > 0) {
        setErrors(importErrors);
        setImporting(false);
        return;
      }

      // Create Level 2 accounts
      const level2IdMap = new Map<string, string>();
      for (const [code, l2] of level2Map) {
        if (l2.id) {
          level2IdMap.set(code, l2.id);
        } else {
          const level1Id = level1IdMap.get(l2.level1Id);
          if (!level1Id) {
            importErrors.push(`Nível 1 não encontrado para Nível 2: ${l2.code}`);
            continue;
          }

          const id = await supabaseSync.createChartOfAccountLevel2({
            level1_id: level1Id,
            code: l2.code,
            name: l2.name,
            type: l2.type,
          });
          if (id) {
            level2IdMap.set(code, id);
          } else {
            importErrors.push(`Erro ao criar Nível 2: ${l2.code} - ${l2.name}`);
          }
        }
      }

      if (importErrors.length > 0) {
        setErrors(importErrors);
        setImporting(false);
        return;
      }

      // Create Level 3 accounts
      let successCount = 0;
      for (const l3 of level3ToCreate) {
        const level2Id = level2IdMap.get(l3.level2Code);
        if (!level2Id) {
          importErrors.push(`Nível 2 não encontrado para Nível 3: ${l3.code}`);
          continue;
        }

        const id = await supabaseSync.createChartOfAccount({
          level2_id: level2Id,
          code: l3.code,
          name: l3.name,
          type: l3.type,
        });

        if (id) {
          successCount++;
        } else {
          importErrors.push(`Erro ao criar Nível 3: ${l3.code} - ${l3.name}`);
        }
      }

      if (importErrors.length > 0) {
        setErrors(importErrors);
      }

      if (successCount > 0) {
        await forceRefresh();
        toast({
          title: 'Importação concluída',
          description: `${successCount} contas Nível 3 importadas com sucesso.`,
        });
        onImportSuccess();
        handleOpenChange(false);
      }
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Erro durante a importação']);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Upload className="h-4 w-4 mr-1" /> Importar
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Importar Plano de Contas
            </DialogTitle>
            <DialogDescription>
              Importe sua estrutura de plano de contas via arquivo Excel.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Instructions */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Formato obrigatório:</strong> O arquivo deve conter as colunas: Código Nível 1, Nome Nível 1,
                Código Nível 2, Nome Nível 2, Código Nível 3, Nome Nível 3, Tipo (receita/despesa).
                <br />
                <strong>Todos os campos são obrigatórios.</strong> Códigos devem seguir hierarquia (ex: 1 → 1.1 → 1.1.1).
              </AlertDescription>
            </Alert>

            {/* Download example button */}
            <Button variant="secondary" size="sm" onClick={downloadExampleExcel}>
              <Download className="h-4 w-4 mr-1" /> Baixar Modelo de Exemplo
            </Button>

            {/* File input */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-primary file:text-primary-foreground
                  hover:file:bg-primary/90
                  cursor-pointer"
              />
            </div>

            {/* Errors */}
            {errors.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-destructive font-medium">
                  <X className="h-4 w-4" />
                  <span>Importação não pode ser realizada. Corrija os erros abaixo:</span>
                </div>
                <ScrollArea className="h-40 border rounded-md p-3 bg-destructive/5">
                  <div className="space-y-1 text-sm">
                    {errors.map((error, i) => (
                      <div key={i} className="text-destructive">
                        {error}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Preview */}
            {previewData.length > 0 && errors.length === 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-green-600 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{previewData.length} registros válidos prontos para importar</span>
                </div>
                <ScrollArea className="h-40 border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-2 py-1 font-medium">Cód. L1</th>
                        <th className="text-left px-2 py-1 font-medium">Cód. L2</th>
                        <th className="text-left px-2 py-1 font-medium">Cód. L3</th>
                        <th className="text-left px-2 py-1 font-medium">Nome L3</th>
                        <th className="text-left px-2 py-1 font-medium">Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 10).map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">{row.codigoNivel1}</td>
                          <td className="px-2 py-1">{row.codigoNivel2}</td>
                          <td className="px-2 py-1">{row.codigoNivel3}</td>
                          <td className="px-2 py-1">{row.nomeNivel3}</td>
                          <td className="px-2 py-1">
                            <span
                              className={`px-1.5 py-0.5 rounded text-xs ${
                                row.tipo === 'receita' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {row.tipo}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {previewData.length > 10 && (
                        <tr className="border-t">
                          <td colSpan={5} className="px-2 py-1 text-muted-foreground italic">
                            ... e mais {previewData.length - 10} registros
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleImport}
              disabled={importing || previewData.length === 0 || errors.length > 0}
            >
              {importing ? 'Importando...' : `Importar ${previewData.length} registros`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
